var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DateUtils } from '../util/DateUtils';
import 'dayjs/locale/pt-br';
dayjs.extend(customParseFormat);
export var DateSelector = function (_a) {
    var _b = _a.inputFormat, inputFormat = _b === void 0 ? 'DD/MM/YYYY' : _b, _c = _a.disableMaskedInput, disableMaskedInput = _c === void 0 ? false : _c, _d = _a.disableFuture, disableFuture = _d === void 0 ? true : _d, _e = _a.disablePast, disablePast = _e === void 0 ? false : _e, _f = _a.variant, variant = _f === void 0 ? 'outlined' : _f, _g = _a.readOnly, readOnly = _g === void 0 ? false : _g, _h = _a.placeholder, placeholder = _h === void 0 ? '' : _h, _j = _a.disabled, disabled = _j === void 0 ? false : _j, _k = _a.required, required = _k === void 0 ? true : _k, fieldError = _a.fieldError, onChange = _a.onChange, label = _a.label, name = _a.name, value = _a.value, minDate = _a.minDate, maxDate = _a.maxDate;
    //region Constants
    var INVALID_DATE = 'Data inválida';
    var YEAR_LENGTH = 4;
    var _l = useState(''), validationError = _l[0], setValidationError = _l[1];
    var composing = useRef(false);
    var _m = useState(''), inputValue = _m[0], setInputValue = _m[1];
    var _o = useState(false), suppressClearOnParentNull = _o[0], setSuppressClearOnParentNull = _o[1];
    //endregion
    //region Hooks
    useEffect(function () {
        if (value) {
            var formatted = dayjs(value).locale('pt-br').format(inputFormat);
            setInputValue(formatted);
            if (suppressClearOnParentNull)
                setSuppressClearOnParentNull(false);
        }
        else if (!suppressClearOnParentNull) {
            setInputValue('');
        }
    }, [value, inputFormat, suppressClearOnParentNull]);
    //endregion
    //region Handles
    var lengthFormat = useMemo(function () {
        var _a;
        var pattern = inputFormat === null || inputFormat === void 0 ? void 0 : inputFormat.replaceAll('/', '').replaceAll('-', '');
        return (_a = pattern === null || pattern === void 0 ? void 0 : pattern.length) !== null && _a !== void 0 ? _a : 0;
    }, [inputFormat]);
    var error = useMemo(function () {
        return (fieldError ? fieldError["".concat(name, "Error")] || '' : '');
    }, [fieldError, name]);
    var onValidate = function () {
        if (value) {
            var dataAtual = DateUtils.getDateJs();
            var dataSelecionada = dayjs(value);
            if (!dataSelecionada.isValid()) {
                setValidationError(INVALID_DATE);
                return;
            }
            var isEqual = dataSelecionada.format('YYYY-MM-DD') === dataAtual.format('YYYY-MM-DD');
            if (disableFuture) {
                if (!isEqual && dataSelecionada.isAfter(dataAtual)) {
                    setValidationError('A data não pode ser maior que a data atual.');
                    setTimeout(function () { return setValidationError(''); }, 5000);
                }
            }
            if (disablePast) {
                if (!isEqual && dataSelecionada.isBefore(dataAtual)) {
                    setValidationError('A data não pode ser anterior à data atual.');
                    setTimeout(function () { return setValidationError(''); }, 5000);
                }
            }
        }
    };
    var parseInputToDate = function (raw, isBlur) {
        if (isBlur === void 0) { isBlur = false; }
        if (!raw)
            return null;
        var lowerRaw = raw.trim().toLowerCase();
        var matchText = lowerRaw.match(/^([a-zç]+)[/\s-]*(?:(\d{2,4}))?$/);
        if (matchText) {
            if (!isBlur && (!matchText[2] || matchText[2].length < 4)) {
                // Wait for the user to finish typing the full year before aggressively formatting
            }
            else {
                var monthStr_1 = matchText[1];
                var yearPart = matchText[2] ? parseInt(matchText[2], 10) : new Date().getFullYear();
                if (yearPart < 100)
                    yearPart += 2000;
                var monthsStrArray = [
                    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
                    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
                ];
                var monthIndex = -1;
                if (monthStr_1.length >= 3) {
                    monthIndex = monthsStrArray.findIndex(function (m) { return m.startsWith(monthStr_1) || (m === 'março' && 'marco'.startsWith(monthStr_1)); });
                }
                if (monthIndex !== -1) {
                    return new Date(yearPart, monthIndex, 1);
                }
            }
        }
        var matchDigits;
        if (isBlur) {
            matchDigits = lowerRaw.match(/^(\d{1,2})[/\s-]*(?:(\d{2,4}))?$/);
        }
        else {
            matchDigits = lowerRaw.match(/^(\d{1,2})[/\s-]+(?:(\d{2,4}))?$/);
        }
        if (matchDigits) {
            if (!isBlur && (!matchDigits[2] || matchDigits[2].length < 4)) {
                // Wait for the user to finish typing the full year before aggressively formatting
            }
            else {
                var monthNum = parseInt(matchDigits[1], 10);
                var yearPart = matchDigits[2] ? parseInt(matchDigits[2], 10) : new Date().getFullYear();
                if (yearPart < 100)
                    yearPart += 2000;
                if (monthNum >= 1 && monthNum <= 12) {
                    return new Date(yearPart, monthNum - 1, 1);
                }
            }
        }
        var candidateStrict = dayjs(lowerRaw, inputFormat, 'pt-br', true);
        if (candidateStrict.isValid()) {
            return candidateStrict.toDate();
        }
        var s = raw.trim();
        s = s.replace(/-/g, '/');
        s = s.replace(/\s+/g, '');
        var digits = s.replace(/\D/g, '');
        if (digits.length > lengthFormat) {
            return null;
        }
        if (lengthFormat === YEAR_LENGTH && digits.length === YEAR_LENGTH) {
            var candidate = dayjs("01/01/".concat(digits), 'DD/MM/YYYY', true);
            return candidate.isValid() ? candidate.toDate() : null;
        }
        if (/^\d{8}$/.test(digits)) {
            var d = digits.substring(0, 2);
            var m = digits.substring(2, 4);
            var y = digits.substring(4, 8);
            var candidate = dayjs("".concat(d, "/").concat(m, "/").concat(y), 'DD/MM/YYYY', true);
            return candidate.isValid() ? candidate.toDate() : null;
        }
        if (/^(\d{2})(\d{4})$/.test(digits)) {
            var m = digits.substring(0, 2);
            var y = digits.substring(2, 6);
            var candidate = dayjs("01/".concat(m, "/").concat(y), 'DD/MM/YYYY', true);
            return candidate.isValid() ? candidate.toDate() : null;
        }
        if (/^(\d{2})(\d{2})$/.test(digits)) {
            var m = digits.substring(0, 2);
            var y = "20" + digits.substring(2, 4);
            var candidate = dayjs("01/".concat(m, "/").concat(y), 'DD/MM/YYYY', true);
            return candidate.isValid() ? candidate.toDate() : null;
        }
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
            var parts = s.split('/');
            var dayPart = parts[0];
            var monthPart = parts[1];
            var yearPart = parts[2];
            var candidate = dayjs("".concat(dayPart.padStart(2, '0'), "/").concat(monthPart.padStart(2, '0'), "/").concat(yearPart), 'DD/MM/YYYY', true);
            return candidate.isValid() ? candidate.toDate() : null;
        }
        return null;
    };
    var handleRawInputChange = function (event) {
        var _a, _b;
        var raw = (_b = (_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        if (suppressClearOnParentNull) {
            setSuppressClearOnParentNull(false);
        }
        if (disableMaskedInput) {
            setInputValue(raw);
            handleMaybeDate(raw);
            return;
        }
        var initialCleaned = raw.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
        var formattedCurrent = value ? dayjs(value).locale('pt-br').format(inputFormat) : null;
        if (formattedCurrent && inputValue === formattedCurrent) {
            if (raw.length > formattedCurrent.length && raw.startsWith(formattedCurrent)) {
                setValidationError(INVALID_DATE);
                setInputValue(initialCleaned);
                return;
            }
        }
        var cleaned = handleCleaned(initialCleaned);
        if (!cleaned) {
            setInputValue('');
            setValidationError('');
            return;
        }
        setValidationError('');
        setInputValue(cleaned);
        handleMaybeDate(cleaned);
    };
    var handleCleaned = function (cleaned) {
        if (cleaned.includes('/')) {
            var parts = cleaned.split('/');
            var dayPart = (parts[0] || '').replace(/\D/g, '').substring(0, 2);
            var monthPart = (parts[1] || '').replace(/\D/g, '').substring(0, 2);
            var yearPart = (parts.slice(2).join('') || '').replace(/\D/g, '').substring(0, 4);
            var rebuilt = [dayPart, monthPart].filter(function (p) { return p.length > 0; }).join('/');
            var secondPart = (parts.length > 2 ? '/' + yearPart : '');
            return rebuilt + (rebuilt && yearPart ? '/' + yearPart : secondPart);
        }
        else {
            var digitsOnly = cleaned.replace(/\D/g, '');
            if (digitsOnly.length > 8) {
                digitsOnly = digitsOnly.substring(0, 8);
            }
            return digitsOnly;
        }
    };
    var handleMaybeDate = function (cleaned) {
        var maybeDate = parseInputToDate(cleaned, false);
        if (maybeDate) {
            onChange === null || onChange === void 0 ? void 0 : onChange(maybeDate);
            setInputValue(dayjs(maybeDate).locale('pt-br').format(inputFormat));
            setValidationError('');
        }
    };
    var handleKeyDown = useCallback(function (e) {
        if (disableMaskedInput)
            return;
        if (e.ctrlKey || e.metaKey || e.altKey)
            return;
        var allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
        if (allowedKeys.includes(e.key))
            return;
        var target = e === null || e === void 0 ? void 0 : e.target;
        var raw = "".concat(target === null || target === void 0 ? void 0 : target.value);
        raw = raw === null || raw === void 0 ? void 0 : raw.replace(/\D/g, '');
        if ((raw === null || raw === void 0 ? void 0 : raw.length) >= lengthFormat) {
            e.preventDefault();
        }
        if (/^\d$/.test(e.key) || e.key === '/' || e.key === '-')
            return;
        if (composing.current)
            return;
        e.preventDefault();
    }, [lengthFormat]);
    var handlePaste = useCallback(function (e) {
        var _a, _b;
        if (disableMaskedInput)
            return;
        e.preventDefault();
        var text = e.clipboardData.getData('text') || '';
        var cleanedPaste = text.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
        var target = e.target;
        var start = (_a = target.selectionStart) !== null && _a !== void 0 ? _a : target.value.length;
        var end = (_b = target.selectionEnd) !== null && _b !== void 0 ? _b : target.value.length;
        var newValue = target.value.substring(0, start) + cleanedPaste + target.value.substring(end);
        handleRawInputChange({ target: { value: newValue } });
    }, []);
    var handleCompositionStart = useCallback(function () {
        composing.current = true;
    }, []);
    var handleCompositionEnd = useCallback(function (e) {
        composing.current = false;
        handleRawInputChange({ target: { value: e.target.value } });
    }, []);
    var handleBlurInput = function () {
        var maybeDate = parseInputToDate(inputValue, true);
        if (maybeDate) {
            var formatted = dayjs(maybeDate).locale('pt-br').format(inputFormat);
            setInputValue(formatted);
            onChange === null || onChange === void 0 ? void 0 : onChange(maybeDate);
            setValidationError('');
        }
        else if (inputValue && inputValue.length > 0) {
            var digits = inputValue.replace(/\D/g, '');
            var currentYear = DateUtils.getDateJs().year();
            if (digits.length > lengthFormat) {
                var yearPart = digits.substring(digits.length - 4);
                var yearNum = parseInt(yearPart, 10);
                if (!isNaN(yearNum) && yearNum > currentYear) {
                    setValidationError('Ano não pode ser superior ao ano atual.');
                }
                else {
                    setValidationError(INVALID_DATE);
                }
            }
            else {
                setValidationError(INVALID_DATE);
            }
            setSuppressClearOnParentNull(true);
            onChange === null || onChange === void 0 ? void 0 : onChange(null);
        }
        onValidate();
    };
    var renderInput = function (params) {
        return (_jsx(TextField, __assign({}, params, { onCompositionStart: handleCompositionStart, onCompositionEnd: handleCompositionEnd, inputProps: __assign(__assign({}, params.inputProps), { readOnly: readOnly }), helperText: validationError || error, error: !!(validationError || error), onChange: handleRawInputChange, onKeyDown: handleKeyDown, placeholder: placeholder, onBlur: handleBlurInput, onPaste: handlePaste, required: required, value: inputValue, variant: variant, fullWidth: true })));
    };
    var handleChange = function (value) {
        var date = value ? value.toDate() : null;
        onChange === null || onChange === void 0 ? void 0 : onChange(date);
        setInputValue(value ? dayjs(value).locale('pt-br').format(inputFormat) : '');
    };
    //endregion
    return (_jsx(LocalizationProvider, __assign({ dateAdapter: AdapterDayjs, adapterLocale: 'pt-Br' }, { children: _jsx(DatePicker, { renderInput: function (params) { return renderInput(params); }, onChange: function (value) { return handleChange(value); }, minDate: minDate ? dayjs(minDate) : undefined, maxDate: maxDate ? dayjs(maxDate) : undefined, disableMaskedInput: disableMaskedInput, disableFuture: disableFuture, disablePast: disablePast, inputFormat: inputFormat, value: value || null, disabled: disabled, label: label }) })));
};
